/**
 * DetailPageSurface - THE canvas-and-panes shell for a detail page.
 *
 * The work in the middle is the CANVAS - a tab bar and whichever tab is open,
 * or a selected finding and its decisions - full-bleed and always there, with
 * glass panes floating over its left and right edges. Panes dock, stack,
 * reorder by dragging their header, resize from their inner edge, and persist
 * per user (and, for an entity-scoped surface, per record).
 *
 * The shell owns ARRANGEMENT ONLY. Each pane's CONTENT is passed in, so the
 * context facts, the connected work, the audit log and the reader stay in
 * their own files and this one never grows domain logic.
 *
 * It was the matter workspace's shell, and review had a near-identical copy
 * of it. The copy had drifted in two ways that were visible on screen - no
 * pane splitters, and a canvas inset on two sides instead of four - so there
 * is now one, generic over the page's pane ids, taking its bounds and its
 * `data-testid` prefix from the surface config. See `defineDetailSurface`.
 */

import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Panel, { PanelDropZone } from './Panel';
import { UsePanelSurfaceResult } from './usePanelSurface';
import { DockSide, DOCK_INSET } from './panelLayout';
import usePanelDrag from './usePanelDrag';
import PaneSplitter from './PaneSplitter';
import { DetailSurfaceDefinition } from './defineDetailSurface';

export interface DetailPane<Id extends string> {
  id: Id;
  title: React.ReactNode;
  icon?: React.ReactNode;
  /** Extra header controls, left of the close button. */
  actions?: React.ReactNode;
  /**
   * The pane's content, or a THUNK returning it.
   *
   * A thunk is only called while the pane is on screen, so a pane whose body
   * is expensive to build - a list that maps a thousand rows into elements, a
   * viewer that wants a blob - costs nothing on a page where it stays closed.
   * Everything a body needs is still read from the page's own render, so
   * nothing goes stale: the thunk runs on every render where the pane shows.
   */
  body: React.ReactNode | (() => React.ReactNode);
}

/** A thunk body is built only when the pane is actually on screen. */
const renderBody = <Id extends string>(pane: DetailPane<Id>): React.ReactNode =>
  typeof pane.body === 'function' ? pane.body() : pane.body;

export interface DetailPageSurfaceProps<Id extends string> {
  /** The page's surface config - supplies the bounds and the testid prefix. */
  surface: DetailSurfaceDefinition<Id>;
  /**
   * The surface's layout state.
   *
   * Owned by the PARENT, not this shell: opening a document is an action of
   * the page (someone pressed a row in the Documents tab), and the reader has
   * to appear in response. A shell that hid its own state could not be asked.
   */
  layoutApi: UsePanelSurfaceResult<Id>;
  /**
   * The panes this page actually produces.
   *
   * NOT every declared pane: a page passes what it has, and a pane id in the
   * config with nothing here simply never draws. That is what lets a page
   * bind a pane it fills only under some condition - and why an empty
   * placeholder component for a pane nothing opens is never needed.
   */
  panes: DetailPane<Id>[];
  /** The canvas - the tab bar and the active tab. */
  children: React.ReactNode;
  /** Height of the shell, so its children can scroll inside it. */
  height?: string;
  /**
   * Extra classes for the CANVAS box (not the shell) - opt-in, default none.
   *
   * A page whose canvas is a document rather than a set of cards wants the
   * background to BE the document's paper, not the app's soft `bg-bg` showing
   * through around a white sheet. Drafting passes white here. Every other
   * surface passes nothing and keeps `bg-bg`.
   */
  canvasClassName?: string;
  /**
   * Let the canvas run to the shell's edge on the sides where no pane is
   * docked - opt-in, default false (the four-sided DOCK_INSET below).
   *
   * Only the free sides bleed: a side with a pane on it keeps its full
   * `readingInset`, or the pane would sit flush against the text. Top and
   * bottom always bleed when this is on, since nothing docks there.
   */
  canvasBleed?: boolean;
}

function DetailPageSurface<Id extends string>({
  surface,
  layoutApi,
  panes,
  children,
  height,
  canvasClassName,
  canvasBleed = false,
}: DetailPageSurfaceProps<Id>) {
  const { t } = useTranslation();
  const { bounds } = surface;
  const name = surface.config.name;
  // One ref per dock column, so a splitter can measure the column it is in.
  const leftColumn = useRef<HTMLDivElement | null>(null);
  const rightColumn = useRef<HTMLDivElement | null>(null);
  const paneFor = useCallback(
    (id: Id) => panes.find((p) => p.id === id),
    [panes]
  );

  /**
   * The drag gesture, from the ONE shared implementation - including the
   * window-level net, without which a drag can be started and never finished.
   * See `usePanelDrag`.
   */
  const drag = usePanelDrag<Id>(
    useCallback(
      (id, target) => layoutApi.move(id, target.dock, target.index),
      [layoutApi]
    ),
    !layoutApi.singleColumn
  );

  const renderDock = (side: DockSide) => {
    const slots = layoutApi.dock(side);
    if (slots.length === 0 && !drag.active) return null;

    /*
      AN EMPTY DOCK NEEDS A WIDTH, or you cannot drop into it.

      This column is `absolute` with no width of its own - it takes the width
      of the `Panel` inside it. During a drag the OPPOSITE dock is empty, so
      there is no panel, and the only thing in the column is the drop zone:
      a label-width strip hard against the screen edge. It looked like a drop
      target and behaved like one only if you hit those few pixels, which is
      why moving a pane to the other side "showed both zones" and then simply
      did not commit - `zoneAtPoint` was hit-testing a sliver.

      While a drag is live an empty column claims the width the dragged pane
      would occupy, so the target is the size of the thing you are dropping.
    */
    const emptyDockWidth =
      slots.length === 0 && drag.dragging
        ? layoutApi.layout.slots[drag.dragging]?.width
        : undefined;

    // The column's share-of-height per pane, and the element the splitter
    // measures to turn a pixel drag into a fraction of it.
    const heights = layoutApi.heights(side);
    const columnRef = side === 'left' ? leftColumn : rightColumn;

    return (
      <div
        ref={columnRef as React.RefObject<HTMLDivElement>}
        className={clsx(
          'absolute top-0 bottom-0 z-30 flex flex-col gap-3 py-3 pointer-events-none',
          side === 'left' ? 'start-0 ps-3' : 'end-0 pe-3',
          layoutApi.scrolls(side) && 'overflow-y-auto'
        )}
        style={emptyDockWidth ? { width: emptyDockWidth } : undefined}
        data-testid={`${name}-dock-${side}`}
      >
        <PanelDropZone
          dock={side}
          index={0}
          active={drag.active}
          hovered={drag.isHovered(side, 0)}
          label={slots.length === 0 ? t('Drop here') : undefined}
          /* The only zone in an empty column fills it, so the target is the
             whole side rather than a band at the top of it. */
          fill={slots.length === 0}
        />
        {slots.map((slot, index) => {
          const pane = paneFor(slot.id);
          if (!pane) return null;
          return (
            <React.Fragment key={slot.id}>
              {/* A pane's flex-grow IS its stored share of the column. */}
              <div
                className="pointer-events-auto min-h-0 flex"
                style={{ flexGrow: heights[index] ?? 1, flexShrink: 1, flexBasis: 0 }}
              >
                <Panel
                  id={slot.id}
                  bounds={bounds[slot.id]}
                  // The DOCK's width: a column of panes at different widths
                  // reads as broken.
                  width={layoutApi.width(side)}
                  dock={side}
                  title={pane.title}
                  icon={pane.icon}
                  actions={pane.actions}
                  zIndex={layoutApi.zIndexOf(slot.id)}
                  onClose={() => layoutApi.setVisible(slot.id, false)}
                  onResize={(next) => layoutApi.resize(slot.id, next)}
                  onFocus={() => layoutApi.focus(slot.id)}
                  draggable={!layoutApi.singleColumn}
                  onDragStart={() => drag.onDragStart(slot.id)}
                  onDragMove={drag.onDragMove}
                  onDragEnd={drag.onDragEnd}
                >
                  {renderBody(pane)}
                </Panel>
              </div>
              <PanelDropZone
                dock={side}
                index={index + 1}
                active={drag.active}
                hovered={drag.isHovered(side, index + 1)}
              />
              {/* Between two stacked panes only, and never while a pane is
                  being dragged between docks - the drop zone owns that gap. */}
              {!drag.active && index < slots.length - 1 && (
                <PaneSplitter
                  columnRef={columnRef as React.RefObject<HTMLElement>}
                  ariaLabel={t('Resize panes')}
                  onResize={(delta) => layoutApi.resizeSplit(side, index, delta)}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // A narrow viewport shows one pane as a sheet; two stacked on a phone is
  // nonsense, and the canvas would be unusable behind them.
  const sheet = layoutApi.sheet ? paneFor(layoutApi.sheet) : null;

  return (
    <div
      className="relative flex-1 min-h-0 bg-bg overflow-hidden"
      style={height ? { height } : undefined}
      data-testid={`${name}-surface`}
    >
      {/*
        ONE GRID. The canvas box is inset to exactly where a docked pane sits,
        on all four sides, so panes and canvas share a top baseline, a bottom
        edge and matching gutters.

        The docks are `py-3` with `ps-3` / `pe-3` - DOCK_INSET on every side.
        The canvas used to be inset horizontally ONLY, and only by
        `readingInset`, which is 0 when nothing is docked. So with no pane open
        the content ran flush into the window edge while the pane column stayed
        a contained card, and the canvas always started 12px above the panes'
        top edge. Hence the lopsided, off-baseline look. (Review still had that
        version until this shell was shared; it is fixed for both here.)

        `Math.max(inset, DOCK_INSET)` keeps the pane-clearing behaviour when
        one is open - `readingInset` already includes the 12px dock inset plus
        a 12px gap - and falls back to the plain gutter when none is.
      */}
      <div
        className={clsx(
          'absolute inset-0 flex flex-col transition-[padding] duration-300 ease-slide',
          canvasClassName
        )}
        style={{
          paddingTop: canvasBleed ? 0 : DOCK_INSET,
          paddingBottom: canvasBleed ? 0 : DOCK_INSET,
          // Logical, not physical: the dock columns are placed with start-0 /
          // end-0 (renderDock), so the canvas must clear the SAME logical edges.
          // With physical paddingLeft/Right the inset lands on the opposite side
          // from the dock in RTL, and the pane covers the content. inset('left')
          // pairs with start (side 'left' -> start-0), inset('right') with end.
          paddingInlineStart: canvasBleed
            ? layoutApi.inset('left')
            : Math.max(layoutApi.inset('left'), DOCK_INSET),
          paddingInlineEnd: canvasBleed
            ? layoutApi.inset('right')
            : Math.max(layoutApi.inset('right'), DOCK_INSET),
        }}
        data-testid={`${name}-canvas`}
      >
        {children}
      </div>

      {renderDock('left')}
      {renderDock('right')}

      {sheet && (
        <div
          className="absolute inset-0 z-40 p-3"
          style={{ paddingTop: DOCK_INSET }}
          data-testid={`${name}-sheet`}
        >
          <Panel
            id={sheet.id}
            bounds={bounds[sheet.id]}
            width={0}
            dock="right"
            sheet
            title={sheet.title}
            icon={sheet.icon}
            actions={sheet.actions}
            onClose={() => layoutApi.setVisible(sheet.id, false)}
          >
            {renderBody(sheet)}
          </Panel>
        </div>
      )}
    </div>
  );
}

export default DetailPageSurface;
